import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, notifyDelete } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { LEDGER_CATEGORIES } from "@/lib/sections";
import ExportRangeDialog from "@/components/ExportRangeDialog";
import LedgerTable, { fmtEUR } from "@/components/LedgerTable";
import LedgerDialog from "@/components/LedgerDialog";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { entry_date: today(), payer: "", category: "fundraising", entry_type: "income", amount: 0, method: "cash", notes: "" };

const TOTAL_CARDS = [
  { key: "balance", testid: "ledger-balance", label: "Current balance", icon: Wallet, iconCls: "bg-primary/10 text-primary", valueCls: "" },
  { key: "income", testid: "ledger-income", label: "Total income", icon: TrendingUp, iconCls: "bg-green-100 text-green-700", valueCls: "text-green-700" },
  { key: "expense", testid: "ledger-expense", label: "Total expense", icon: TrendingDown, iconCls: "bg-red-100 text-red-700", valueCls: "text-red-700" },
];

function LedgerTotals({ totals }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {TOTAL_CARDS.map(({ key, testid, label, icon: Icon, iconCls, valueCls }) => (
        <Card key={key} className="card-shadow-soft" data-testid={testid}><CardContent className="p-5">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${iconCls}`}><Icon className="w-5 h-5" /></div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className={`text-3xl font-extrabold ${valueCls}`} style={{ fontFamily: "Outfit" }}>{fmtEUR(totals[key])}</div>
        </CardContent></Card>
      ))}
    </div>
  );
}

export default function Ledger() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/ledger");
    setEntries(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const income = entries.filter((e) => e.entry_type !== "expense").reduce((s, e) => s + e.amount, 0);
    const expense = entries.filter((e) => e.entry_type === "expense").reduce((s, e) => s + e.amount, 0);
    return { income, expense, balance: income - expense };
  }, [entries]);

  const filtered = filter === "all" ? entries : entries.filter((e) => e.category === filter);

  const save = async () => {
    try {
      await api.post("/ledger", { ...form, amount: Number(form.amount) });
      toast.success("Ledger entry saved");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ledger entry?")) return;
    const { data } = await api.delete(`/ledger/${id}`);
    notifyDelete(data, "Entry removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Payments Ledger</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Group finances</h1>
        </div>
        <div className="flex gap-2">
          <ExportRangeDialog path="/export/ledger.csv" filenameBase="ledger" testid="export-ledger-btn" />
          <Button data-testid="add-ledger-btn" onClick={() => { setForm(empty); setOpen(true); }} className="min-h-[44px]">
            <Plus className="w-4 h-4 mr-1.5" /> Add entry
          </Button>
        </div>
      </div>

      <LedgerTotals totals={totals} />

      <div className="flex items-center gap-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Filter</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="ledger-filter" className="w-56 min-h-[44px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {LEDGER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <LedgerTable entries={filtered} onDelete={del} />
      <LedgerDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} onSave={save} />
    </div>
  );
}
